<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import type { SessionInfo, PlayerNote } from '../../types';
  import PlayerAward from '../PlayerAward.svelte';
  import * as apiService from '../../lib/apiService';
  import { showToast } from '../../lib/toast';
  import PhotoSelectionDialog from '../PhotoSelectionDialog.svelte';
  import OnboardingModal from '../OnboardingModal.svelte';
  import { onMount } from 'svelte';
  import { checkOnboardingStep } from '../../lib/utils/onboardingUtils';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../../lib/utils/dialogAnimations';

  export let match;
  export let sessionInfo: SessionInfo;
  
  const dispatch = createEventDispatcher();
    
  // Photo selection for match photo
  let photoDialogOpen = false;
  
  // Share button state
  let isSubmitting = false;
  let showRequirementsDialog = false;
  let requirementsDialogElement: HTMLDialogElement;

  // Todo list checkmarks
  $: hasMatchReport = match.match_report && match.match_report.trim() !== '';
  $: hasMatchPhoto = match.match_photo || match.match_photo_filename;
  $: hasPlayerAwards = match.player_awards && match.player_awards.length > 0;

  // Share button should only be enabled if report and photo are present
  $: canShare = hasMatchPhoto;
  
  function openPhotoDialog() {
    photoDialogOpen = true;
  }

  function closePhotoDialog() {
    photoDialogOpen = false;
  }

  // Handle photo selection
  function handlePhotoSelection(event) {
    const { file, filename, url, type } = event.detail;
    
    match.match_photo = file;
    match.match_photo_filename = filename;
    match.match_photo_type = type;
    
    notifyChange();
  }
  
  // AI report generation
  let isGenerating = false;
  let showAIModal = false;
  let aiGeneratedContent = '';
  let isAdjustingTone = false;
  let formalUsed = false;
  let enthusiasticUsed = false;
  let showEditModal = false;
  let isEditingText = false;
  let editDialogElement: HTMLDialogElement;
  let aiModalDialogElement: HTMLDialogElement;
  
  async function generateReport() {
    if (!match.match_date || !match.opponent_club?.name) {
      alert($_('match.report.fillRequiredFields'));
      return;
    }

    isGenerating = true;
    try {
      await apiService.generateReport({
        match_id: match.id
      }, async (result, status) => {
        if (status === 200) {
          aiGeneratedContent = result.report;
          await openAIModal();
        } else {
          showToast(result.message || $_('match.report.generationFailed'), 'error');
        }
        isGenerating = false;
      });
    } catch (error) {
      console.error('Error generating report:', error);
      showToast($_('match.report.generationFailed'), 'error');
      isGenerating = false;
    }
  }
  
  async function updateReport() {
    if (!match.match_report || !match.match_date || !match.opponent_club?.name) {
      alert($_('match.report.fillRequiredFields'));
      return;
    }

    isGenerating = true;
    try {
      await apiService.updateReport({
        match_id: match.id
      }, async (result, status) => {
        if (status === 200) {
          aiGeneratedContent = result.report;
          await openAIModal();
        } else {
          showToast(result.message || $_('match.report.updateFailed'), 'error');
        }
        isGenerating = false;
      });
    } catch (error) {
      console.error('Error updating report:', error);
      showToast($_('match.report.updateFailed'), 'error');
      isGenerating = false;
    }
  }
  
  async function improveReport(tone: string) {
    if (!match.match_report || !match.match_date || !match.opponent_club?.name) {
      alert($_('match.report.fillRequiredFields'));
      return;
    }

    isAdjustingTone = true;
    if (tone === "formal") formalUsed = true;
    if (tone === "enthusiastic") enthusiasticUsed = true;

    try {
      await apiService.improveReport({
        match_id: match.id,
        tone: tone
      }, async (result, status) => {
        if (status === 200) {
          aiGeneratedContent = result.report;
          await openAIModal();
        } else {
          showToast(result.message || $_('match.report.updateFailed'), 'error');
        }
        isAdjustingTone = false;
      });
    } catch (error) {
      console.error('Error updating report:', error);
      showToast($_('match.report.updateFailed'), 'error');
      isAdjustingTone = false;
    }
  }
  

  async function openAIModal() {
    showAIModal = true;
    await new Promise(resolve => setTimeout(resolve, 0));
    if (aiModalDialogElement) {
      await openDialogWithSlideUp(aiModalDialogElement);
    }
  }

  async function closeAIModal() {
    await closeDialogWithSlideDown(aiModalDialogElement);
    showAIModal = false;
    aiGeneratedContent = '';
  }

  async function handleAIConfirm() {
    match.match_report = aiGeneratedContent;
    match = { ...match };
    await closeAIModal();
    notifyChange();
  }

  async function handleAICancel() {
    await closeAIModal();
  }
    
  function notifyChange() {
    console.log("notifyChange");
    dispatch('change', { match });
  }
  
  async function openEditModal() {
    if (!match.match_report) {
      await generateReport();
    } else {
      showEditModal = true;

      // Wait for DOM update and trigger animation
      await new Promise(resolve => setTimeout(resolve, 0));
      if (editDialogElement) {
        await openDialogWithSlideUp(editDialogElement);
      }
    }
  }

  async function closeEditModal() {
    await closeDialogWithSlideDown(editDialogElement);
    showEditModal = false;
  }
  
  function copyToClipboard() {
    if (match.match_report) {
      navigator.clipboard.writeText(match.match_report)
        .then(() => {
          showToast($_('common.copiedToClipboard'), 'success');
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          showToast($_('common.copyFailed'), 'error');
        });
    }
  }

  // Available awards for players
  const playerAwards = [
    {id: 'player-of-match', label: $_('match.awards.player-of-match'), icon: '👑'},
    // {id: 'best-defense', label: $_('match.awards.best-defense')},
    // {id: 'team-spirit', label: $_('match.awards.team-spirit')},
    // {id: 'best-assist', label: $_('match.awards.best-assist')},
    // {id: 'breakthrough', label: $_('match.awards.breakthrough')},
  ];
  
  // Variable to track current award being edited
  let editingAward = null;
  
  // Find player who received a specific award
  function getPlayerForAward(awardId: string): PlayerNote | null {
    return match.player_awards?.find(award => award.id === awardId) || null;
  }
  
  // Functions for handling player award modal
  function openAwardModal(award) {
    const existingAwardedPlayer = getPlayerForAward(award.id);
    
    editingAward = {
      award: award,
      player: existingAwardedPlayer
    };
  }
  
  function closeAwardModal() {
    editingAward = null;
  }
  
  function handleAwardSubmit(awardedPlayer: PlayerNote) {
    console.log("handleAwardSubmit", awardedPlayer);
    if (!match.player_awards) {
      match.player_awards = [];
    }
    
    // Find and remove any existing player with this award
    match.player_awards = match.player_awards.filter(award => award.id !== editingAward.award.id);
    
    // Add the new awarded player
    match.player_awards.push(awardedPlayer);
    console.log("match.player_awards", match.player_awards);
    match = { ...match };
    closeAwardModal();
    notifyChange();
  }
  
  function deleteAward(awardId: string) {
    console.log("deleteAward", awardId);
    console.log("editingAward", editingAward);
    if (editingAward && editingAward.award) {
      // Remove the player with this award
      match.player_awards = match.player_awards.filter(award => award.id !== awardId);
      console.log("match.player_awards", match.player_awards);
      match = { ...match };
      closeAwardModal();
      notifyChange();
    }
  }

  // Handle share action for report
  async function handleShare() {
    // Check if all requirements are met
    if (!canShare) {
      showRequirementsDialog = true;
      await new Promise(resolve => setTimeout(resolve, 0));
      if (requirementsDialogElement) {
        await openDialogWithSlideUp(requirementsDialogElement);
      }
      return;
    }

    try {
      isSubmitting = true;
      // Notify parent to handle the share action
      dispatch('share', { type: 'report' });
    } catch (error) {
      console.error('Error preparing share:', error);
    } finally {
      isSubmitting = false;
    }
  }

  async function closeRequirementsDialog() {
    await closeDialogWithSlideDown(requirementsDialogElement);
    showRequirementsDialog = false;
  }

  let showOnboardingModal = false;

  async function checkAccountSettingsOnboarding() {
    try {
      const hasSeenOnboarding = await checkOnboardingStep('gameassist_report');
      if (!hasSeenOnboarding) {
        // Show welcome modal after a short delay for better UX
        setTimeout(() => {
          showOnboardingModal = true;
        }, 500);
      }
    } catch (error) {
      console.error('Error checking account settings onboarding:', error);
    }
  }

  function closeOnboardingModal() {
    showOnboardingModal = false;
  }

  onMount(async () => {
    await checkAccountSettingsOnboarding();
  });
</script>


{#if !showAIModal && !photoDialogOpen && !showEditModal}
  
  <article>
    <h5 class="flex items-start">{#if hasMatchPhoto}<span class="mr-2 text text-lg text-success"><ion-icon name="checkbox"></ion-icon></span>{:else}<span class="mr-2 text text-lg text-danger"><ion-icon name="square"></ion-icon></span>{/if}{$_('match.report.matchPhoto')}</h5>
    <div class="">

      <button class="outline full-width" on:click={openPhotoDialog}>
        <ion-icon name="image"></ion-icon>
        {$_('match.report.chooseImage')}
      </button>
      <div class="photo-preview-container">
        {#if match.match_photo}
          <div class="photo-preview">
            <img src={URL.createObjectURL(match.match_photo)} alt="Preview" />
          </div>
          <div class="text text-sm">{match.match_photo.name}</div>
        {:else if match.match_photo_filename}
          <div class="photo-preview">
            {#if match.match_photo_url}
              <img src={match.match_photo_url} alt="Preview" />
            {:else if match.match_photo_filename.includes('/')}
              <!-- User uploaded photo with user_id/filename format -->
              <img src={`/uploads/${match.match_photo_filename}`} alt="Preview" />
            {:else}
              <!-- Stock photo -->
              <img src={`/img/backgrounds/match/${sessionInfo.active_team.club.sport}/${match.match_photo_filename}`} alt="Preview" />
            {/if}
          </div>
          <div class="text text-sm">{match.match_photo_filename.includes('/') ? match.match_photo_filename.split('/').pop() : match.match_photo_filename}</div>
        {:else}
          <div class="text text-sm">{$_('match.report.noPhoto')}</div>
        {/if}
      </div>
    </div>
    </article>

  <article>
    <h5 class="flex items-start"><span class="mr-2 text text-lg text-success">{#if hasMatchReport}<ion-icon name="checkbox"></ion-icon>{:else}<ion-icon name="square-outline"></ion-icon>{/if}</span>{$_('match.report.reportLabel')} ({$_('common.optional')})</h5>
    <div class="">
      {#if match.match_report}
      <button class="text-area-style" on:click={openEditModal}>{match.match_report ? `${match.match_report.substring(0,200)}...` : ''}</button>
      {/if}
      {#if !match.highlights || match.highlights.length == 0}
        <p class="text text-sm">
          <span class="text text-danger">
            <ion-icon name="information-circle-outline"></ion-icon>
          </span>
          {$_('match.report.noHighlights')}
        </p>
          <button class="primary full-width outline" on:click={() => window.location.hash = `#/match/${match.id}/highlights`}>{$_('match.report.go_to_highlights')} 
            <ion-icon name="arrow-forward-outline"></ion-icon>
          </button>
      {/if}
      {#if !match.match_report && (match.highlights && match.highlights.length > 0)}
        <button 
            class="primary outline full-width mb-4"
            on:click|preventDefault|stopPropagation={openEditModal}
          >
          <ion-icon name="color-wand" class="pt-2"></ion-icon>
          {isGenerating ? $_('match.report.generating') : $_('match.report.generateButton')}
        </button>
      {:else if match.match_report}
        <button 
            class="primary outline  full-width mb-4"
            on:click|preventDefault|stopPropagation={updateReport}
          >
          <ion-icon name="color-wand" class="pt-2"></ion-icon>
          {isGenerating ? $_('match.report.updating') : $_('match.report.updateButton')}
        </button>
      {/if}
    </div>
  </article>

  <article>
    <h5 class="flex items-start"><span class="mr-2 text text-lg text-success">{#if hasPlayerAwards}<ion-icon name="checkbox"></ion-icon>{:else}<ion-icon name="square-outline"></ion-icon>{/if}</span>{$_('match.report.playerAwards')} ({$_('common.optional')})</h5>
    <div class="awards-list">
      {#each playerAwards as award}
        {@const awardedPlayer = getPlayerForAward(award.id)}
        <button 
          class="secondary full-width award-button" 
          on:click={() => openAwardModal(award)}
        >
          <span class="award-name">{award.icon} {award.label}</span>
          {#if awardedPlayer}
            <span class="player-name">{awardedPlayer.name}</span>
          {:else}
            <span class="not-assigned">{$_('match.awards.notAssigned')}</span>
          {/if}
        </button>
      {/each}
    </div>
  </article>
{/if}

<!-- Photo selection dialog -->
{#if photoDialogOpen}
    <PhotoSelectionDialog 
      sport={sessionInfo.active_team.club.sport} 
      photoType="match"
      matchId={match.id} 
      on:close={closePhotoDialog}
      on:select={handlePhotoSelection}
    />
{/if}

{#if editingAward}
  <PlayerAward
    award={editingAward.award}
    playerAward={editingAward.player}
    matchId={match.id}
    teamMembers={sessionInfo.active_team.team_members}
    sport={sessionInfo.active_team.club.sport || 'football'}
    on:close={closeAwardModal}
    on:save={(event) => handleAwardSubmit(event.detail)}
    on:delete={(event) => deleteAward(event.detail)}
  />
{/if}

{#if showEditModal}
  <dialog bind:this={editDialogElement} class="slide-up-dialog full-height">
    <article class="mt-0 pt-0">
      <header class="sticky-header p-4">
        <h5>{$_('match.report.editReport')}</h5>
        <button
          class="close"
          on:click={closeEditModal}
          aria-label={$_('common.close')}
        >
          <ion-icon name="close"></ion-icon>
        </button>
      </header>
      {#if isAdjustingTone}
        <div class="loading-container">
          <div aria-busy="true" aria-label={$_('common.loading')}></div>
          <p>{$_('match.report.adjustingTone')}</p>
        </div>
      {:else}
        <textarea 
          bind:value={match.match_report} 
          placeholder={$_('match.report.reportPlaceholder')}
          rows="20"
          on:focus={() => isEditingText = true}
          on:blur={() => isEditingText = false}
        ></textarea>
      {/if}
      {#if !isAdjustingTone && !isEditingText}
      <div role="group">
        <button 
          on:click={() => improveReport("formal")} 
          class="secondary small outline"
          disabled={formalUsed}
        >
          <ion-icon name="arrow-down"></ion-icon> 
          {isAdjustingTone ? $_('common.loading') : $_('match.report.moreFormal')}
        </button>
        <button 
          on:click={() => improveReport("enthusiastic")} 
          class="secondary small outline"
          disabled={enthusiasticUsed}
        >
          {isAdjustingTone ? $_('common.loading') : $_('match.report.moreEnthusiastic')}
          <ion-icon name="arrow-up"></ion-icon> 
        </button>
      </div>
      {/if}
      <footer class="flex space-between mt-0">
        {#if !isAdjustingTone}
          <button on:click={copyToClipboard} class="secondary">
            <ion-icon name="copy"></ion-icon>
            {$_('common.copyToClipboard')}
          </button>
        {/if}
        <button on:click={closeEditModal} class="primary">
          {$_('common.close')}
        </button>
      </footer>
    </article>
  </dialog>
{/if}

{#if showAIModal}
  <dialog bind:this={aiModalDialogElement} class="slide-up-dialog full-height">
    <article class="mt-0 pt-0">
      <header class="sticky-header p-4">
        <h5>{$_('match.report.aiGeneratedReport')}</h5>
        <button
          class="close"
          on:click={handleAICancel}
          aria-label={$_('common.close')}
        >
          <ion-icon name="close"></ion-icon>
        </button>
      </header>
      <textarea bind:value={aiGeneratedContent} rows="20"></textarea>
      <footer class="flex space-between mt-0">
        <button on:click={handleAICancel} class="secondary">
          {$_('match.report.cancelReport')}
        </button>
        <button on:click={handleAIConfirm} class="primary">
          {$_('common.use')}
        </button>
      </footer>
    </article>
  </dialog>
{/if}

<!-- Share button container -->
<div class="sticky-footer">
  <div class="sticky-footer-buttons">
    <button
      class="sticky-footer-btn"
      on:click={handleShare}
      disabled={isSubmitting}
    >
      <ion-icon name="images-outline"></ion-icon>
      {$_('match.navigation.create_report')}
    </button>
  </div>
</div>

<!-- Requirements Dialog -->
{#if showRequirementsDialog}
<dialog bind:this={requirementsDialogElement} class="slide-up-dialog">
  <article class="mt-0">
    <header class="py-4">
      <h5>{$_('match.report.requirementsTitle')}</h5>
    </header>

    <p class="mb-4">{$_('match.report.requirementsMessage')}</p>

    <div class="requirements-list">
      {#if !hasMatchPhoto}
      <div class="requirement-item" class:completed={hasMatchPhoto}>
        <span class="mr-2 text text-lg" class:text-danger={!hasMatchPhoto}>
          <ion-icon name={hasMatchPhoto ? "checkbox" : "square"}></ion-icon>
        </span>
        <span>{$_('match.report.matchPhoto')}</span>
      </div>
      {/if}
    </div>

    <footer class="mt-4">
      <button class="primary full-width" on:click={closeRequirementsDialog}>
        {$_('common.close')}
      </button>
    </footer>
  </article>
</dialog>
{/if}

<!-- Onboarding Modal -->
<OnboardingModal
  isOpen={showOnboardingModal}
  title={$_('onboarding.gameassist_report.title')}
  onClose={closeOnboardingModal}
>
  <p>{$_('onboarding.gameassist_report.info')}</p>
  <ul class="list-disc list-inside">
    <li>{$_('onboarding.gameassist_report.step1')}</li>
    <li>{$_('onboarding.gameassist_report.step2')}</li>
    <li>{$_('onboarding.gameassist_report.step3')}</li>
  </ul>
</OnboardingModal>


<style>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

button.text-area-style {
  font-size: 1rem !important;
  line-height: var(--pico-line-height) !important;
  font-family: inherit !important;
  letter-spacing: inherit !important;
  text-align: left !important;
  line-height: 1.2 !important;
  border: var(--pico-border-width) solid var(--pico-form-element-border-color) !important;
  border-radius: var(--pico-border-radius) !important;
  outline: 0 !important;
  background-color: var(--pico-form-element-background-color) !important;
  box-shadow: var(--pico-box-shadow) !important;
  color: var(--pico-color-text) !important;
  font-weight: var(--pico-font-weight) !important;
  margin-bottom: 1rem !important;
  border-radius: var(--pico-border-radius);
}


.sticky-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

textarea {
  flex: 1;
  resize: none;
  margin: var(--pico-spacing) 0;
}

.close {
  background: none;
  border: none;
  cursor: pointer;
  margin: 0;
  color: var(--pico-primary-text);
}

.close ion-icon {
  font-size: 1.5rem;
}

.awards-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.award-button {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  text-align: left;
}

.not-assigned {
  opacity: 0.6;
  font-style: italic;
}

.photo-preview-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.photo-preview {
  width: 60px;
  height: 40px;
  border-radius: var(--pico-border-radius);
  overflow: hidden;
  flex-shrink: 0;
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: transparent;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.requirement-item {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: var(--pico-border-radius);
  background-color: var(--pico-form-element-background-color);
}

.requirement-item.completed {
  opacity: 0.7;
}

</style> 